// Auto helper (Gambits): regras de cura e suprimentos definidas pelo jogador, comida, respectful hunt e loot.
import type { VocationId } from './vocations.js';

export type Stance = 'offensive' | 'balanced' | 'defensive';

export interface HealSpellDef { words: string; name: string; cost: number; level: number; vocations: VocationId[]; ml: [number, number, number, number]; /** friend: o membro da party mais ferido; party: você e a party por perto */ target?: 'friend' | 'party'; cooldown?: number }
/** Magias de cura do Tibia (fórmula: level*0,2 + ML*a + b até level*0,2 + ML*c + d). */
export const HEAL_SPELLS: HealSpellDef[] = [
  // vocations conforme o Tibia atual (knights não usam Light Healing desde 2010). Fórmulas aproximadas onde o wiki não diz.
  { words: 'exura infir', name: 'Magic Patch', cost: 6, level: 1, vocations: ['sorcerer', 'druid', 'paladin', 'monk'], ml: [1, 4, 1.3, 7] },
  { words: 'exura infir ico', name: 'Bruise Bane', cost: 10, level: 1, vocations: ['knight'], ml: [2, 10, 3, 18] },
  { words: 'exura', name: 'Light Healing', cost: 20, level: 8, vocations: ['sorcerer', 'druid', 'paladin', 'monk'], ml: [1.4, 8, 1.795, 11] },
  { words: 'exura ico', name: 'Wound Cleansing', cost: 40, level: 8, vocations: ['knight'], ml: [4, 25, 7.95, 51] },
  { words: 'exura gran', name: 'Intense Healing', cost: 70, level: 20, vocations: ['sorcerer', 'druid', 'paladin', 'monk'], ml: [3.184, 20, 5.59, 35] },
  { words: 'exura vita', name: 'Ultimate Healing', cost: 160, level: 30, vocations: ['sorcerer', 'druid'], ml: [7.22, 44, 12.79, 79] },
  { words: 'exura san', name: 'Divine Healing', cost: 160, level: 35, vocations: ['paladin'], ml: [18.5, 0, 25, 0] },
  { words: 'exura gran san', name: 'Salvation', cost: 210, level: 60, vocations: ['paladin'], ml: [22, 0, 30, 0] },
  { words: 'exura gran ico', name: 'Intense Wound Cleansing', cost: 200, level: 80, vocations: ['knight'], ml: [8, 50, 15, 100] },
  { words: 'exura gran tio', name: 'Spirit Mend', cost: 210, level: 80, vocations: ['monk'], ml: [10, 60, 16, 110] },
  // spender do monk: a cura cresce com a Harmony (oficial: level/5 + ML*5,7 + 26 até level/5 + ML*10,43 + 62)
  { words: 'exura mas nia', name: 'Mass Spirit Mend', cost: 250, level: 150, vocations: ['monk'], ml: [5.7, 26, 10.43, 62] },
  { words: 'exura med ico', name: 'Fair Wound Cleansing', cost: 90, level: 300, vocations: ['knight'], ml: [8, 50, 15, 100] },
  { words: 'exura max vita', name: 'Restoration', cost: 260, level: 300, vocations: ['druid', 'sorcerer'], ml: [10, 60, 18, 120], cooldown: 6000 },
  // curas de party (só fazem efeito com party na mesma área)
  { words: 'exura sio', name: 'Heal Friend', cost: 120, level: 18, vocations: ['druid'], ml: [10, 0, 14, 0], target: 'friend' },
  { words: 'exura tio sio', name: 'Restore Balance', cost: 120, level: 18, vocations: ['monk'], ml: [10, 0, 14, 0], target: 'friend' },
  { words: 'exura gran sio', name: "Nature's Embrace", cost: 400, level: 300, vocations: ['druid'], ml: [20, 0, 28, 0], target: 'friend', cooldown: 60_000 },
  { words: 'exura gran mas res', name: 'Mass Healing', cost: 150, level: 36, vocations: ['druid'], ml: [5.7, 26, 10.43, 62], target: 'party' },
  { words: 'utura mas sio', name: 'Heal Party', cost: 120, level: 32, vocations: ['druid'], ml: [3, 15, 6, 30], target: 'party' },
];
/** Train/Protect/Enchant Party (Canary): bônus de 2 minutos para você e a party por perto; mana cresce com quem recebe */
export const PARTY_BUFFS: Record<string, { name: string; base: number; melee?: number; distance?: number; shield?: number; ml?: number; ms: number; vocation: VocationId; level: number }> = {
  'utito mas sio': { name: 'Train Party', base: 60, melee: 3, distance: 3, ms: 120_000, vocation: 'knight', level: 32 },
  'utamo mas sio': { name: 'Protect Party', base: 90, shield: 3, ms: 120_000, vocation: 'paladin', level: 32 },
  'utori mas sio': { name: 'Enchant Party', base: 120, ml: 1, ms: 120_000, vocation: 'sorcerer', level: 32 },
};
export const partyBuffMana = (base: number, n: number) => Math.ceil(Math.pow(0.9, n - 1) * base * n);
/** Challenge e variações (puxam os monstros em volta para você) */
export const CHALLENGE_SPELLS = ['exeta res', 'exeta amp res', 'exana amp res', 'exori mas res'];
/** Expose Weakness (+5% de dano recebido) e Sap Strength (-10% de dano causado) nos monstros em volta do alvo por 16 s */
export const DEBUFF_SPELLS: Record<string, { kind: 'expose' | 'sap'; mana: number; level: number; cd: number }> = {
  'exori moe': { kind: 'expose', mana: 400, level: 275, cd: 12_000 },
  'exori kor': { kind: 'sap', mana: 300, level: 275, cd: 12_000 },
};
/** Familiares (Canary): 15 minutos, recarga de 30 minutos */
export const FAMILIARS: Record<string, { monster: string; vocation: VocationId; mana: number }> = {
  'utevo gran res eq': { monster: 'Knight familiar', vocation: 'knight', mana: 1000 },
  'utevo gran res sac': { monster: 'Paladin familiar', vocation: 'paladin', mana: 2000 },
  'utevo gran res ven': { monster: 'Sorcerer familiar', vocation: 'sorcerer', mana: 3000 },
  'utevo gran res dru': { monster: 'Druid familiar', vocation: 'druid', mana: 3000 },
  'utevo gran res tio': { monster: 'Monk familiar', vocation: 'monk', mana: 1500 },
};
export const FAMILIAR_MS = 15 * 60_000;
export const FAMILIAR_CD_MS = 30 * 60_000;

export interface AttackSpellDef {
  words: string; name: string; element: 'fire' | 'ice' | 'energy' | 'earth' | 'physical' | 'holy'; cost: number; level: number;
  vocations: VocationId[]; range: number;
  /** magic: fórmula de magic level; weapon: multiplica o dano máximo da arma (knights, paladins). */
  kind: 'magic' | 'weapon'; mult?: [number, number];
}
/** Magias de ataque de alvo único (2 s de espera entre elas). Fórmulas das de arma são aproximadas. */
export const ATTACK_SPELLS: AttackSpellDef[] = [
  { words: 'exori flam', name: 'Flame Strike', element: 'fire', cost: 20, level: 8, vocations: ['sorcerer', 'druid'], range: 3, kind: 'magic' },
  { words: 'exori frigo', name: 'Ice Strike', element: 'ice', cost: 20, level: 8, vocations: ['sorcerer', 'druid'], range: 3, kind: 'magic' },
  { words: 'exori vis', name: 'Energy Strike', element: 'energy', cost: 20, level: 12, vocations: ['sorcerer', 'druid'], range: 3, kind: 'magic' },
  { words: 'exori tera', name: 'Terra Strike', element: 'earth', cost: 20, level: 13, vocations: ['sorcerer', 'druid'], range: 3, kind: 'magic' },
  { words: 'exori ico', name: 'Brutal Strike', element: 'physical', cost: 30, level: 16, vocations: ['knight'], range: 1, kind: 'weapon', mult: [0.9, 1.6] },
  { words: 'exori hur', name: 'Whirlwind Throw', element: 'physical', cost: 40, level: 28, vocations: ['knight'], range: 5, kind: 'weapon', mult: [0.8, 1.4] },
  { words: 'exori con', name: 'Ethereal Spear', element: 'physical', cost: 25, level: 23, vocations: ['paladin'], range: 6, kind: 'weapon', mult: [0.9, 1.5] },
  { words: 'exori san', name: 'Divine Missile', element: 'holy', cost: 20, level: 40, vocations: ['paladin'], range: 4, kind: 'magic' },
];
export function strikeDamage(level: number, ml: number): [number, number] {
  return [Math.floor(level * 0.2 + ml * 1.403 + 8), Math.floor(level * 0.2 + ml * 2.203 + 13)];
}

export function healAmount(s: HealSpellDef, level: number, ml: number): [number, number] {
  const l = level * 0.2;
  return [Math.floor(l + ml * s.ml[0] + s.ml[1]), Math.floor(l + ml * s.ml[2] + s.ml[3])];
}

/** Tempo de alimentação de cada comida em segundos (Tibia), máximo acumulado de 1200 s. */
export const FOOD_SECONDS: Record<string, number> = {
  meat: 180, fish: 144, salmon: 120, ham: 360, 'dragon ham': 720, orange: 156, banana: 96, carrot: 96, cookie: 24,
  bread: 120, roll: 36, egg: 72, cheese: 108, 'white mushroom': 108, 'red mushroom': 48, 'brown mushroom': 264,
  'fire mushroom': 432, apple: 72, grape: 108,
};
export const FOOD_MAX = 1200;

/**
 * Um espaço da barra de ações (1 a 12). A caça automática percorre a barra da esquerda para a direita
 * e usa o primeiro que estiver liberado (cooldown) e cumprir as condições do Gambits.
 * Curas e poções usam "atributo abaixo de X%"; ataques usam "quantidade mínima de monstros" e "mana mínima".
 */
export interface HotbarSlot {
  use: { spell: string } | { item: number };
  /** A caça automática pode usar este espaço. */
  auto: boolean;
  stat: 'hp' | 'mana';
  below: number;
  minMonsters: number;
  minMana: number;
  /** monk: só usa um spender com pelo menos esta Harmony (0 a 5; sem valor = 5) */
  minHarmony?: number;
  /** curas de membro da party (Exura Sio e parecidas): nome de quem curar; sem valor = o membro mais ferido */
  who?: string;
}
export type HotbarEntry = HotbarSlot;

/** Condições que o Auto Helper cura sozinho e as magias oficiais que curam cada uma (em ordem de preferência). */
export const CONDITION_CURES: Array<{ id: string; name: string; element?: string; spells: string[]; desc: string }> = [
  { id: 'poison', name: 'Envenenado', element: 'earth', spells: ['exana pox'], desc: 'Veneno tira vida aos poucos (dano de terra).' },
  { id: 'burning', name: 'Queimando', element: 'fire', spells: ['exana flam'], desc: 'Queimadura tira vida aos poucos (dano de fogo).' },
  { id: 'electrified', name: 'Eletrificado', element: 'energy', spells: ['exana vis'], desc: 'Eletrificação tira vida aos poucos (dano de energia).' },
  { id: 'bleeding', name: 'Sangrando', element: 'physical', spells: ['exana kor'], desc: 'Sangramento tira vida aos poucos (dano físico).' },
  { id: 'cursed', name: 'Amaldiçoado', element: 'death', spells: ['exana mort'], desc: 'Maldição tira vida aos poucos (dano de morte).' },
  { id: 'paralyzed', name: 'Paralisado', spells: ['utani gran hur', 'utani hur', 'utani tempo hur'], desc: 'Paralisia deixa o personagem bem mais lento. Qualquer haste remove a paralisia.' },
  { id: 'freezing', name: 'Congelando', element: 'ice', spells: [], desc: 'Não existe magia que cure congelamento no Tibia: ele acaba sozinho.' },
  { id: 'dazzled', name: 'Ofuscado', element: 'holy', spells: [], desc: 'Não existe magia que cure ofuscamento no Tibia: ele acaba sozinho.' },
];

export interface HelperSettings {
  stance: Stance;
  autoEat: boolean;
  respectful: boolean;
  /** Itens que a caça automática não pega (filtro de loot). */
  skipLoot: number[];
  /** Itens que a venda automática / "vender loot comum" nunca vende. */
  keepItems: number[];
  /** Mochila cheia: vende o loot comum sozinho quando a venda na hunt estiver liberada. */
  sellWhenFull: boolean;
  /** venda automática por tempo na hunt (a cada 20 min free / 10 min VIP), menos os itens de keepItems e os com raridade */
  autoSell?: boolean;
  /** usar sozinho a poção de skill da vocation (Berserk, Mastermind, Bullseye) quando o efeito acabar, se tiver na mochila */
  autoSkillPotion?: boolean;
  /** Utamo Vita automático (sorcerer e druid): mantém o Magic Shield sempre ativo, renovando antes de acabar */
  autoMagicShield?: boolean;
  /** PvP: o que fazer quando outro jogador ataca — revidar, fugir para o templo ou ignorar e continuar caçando */
  pvpReact?: 'fight' | 'flee' | 'ignore';
  /** Anti-Red: nunca usar magia/runa em área com outro jogador atacável na tela (menos quando está revidando) */
  antiRed?: boolean;
  /** Barra de ações 1 a 12 (ordem de prioridade da caça automática). */
  hotbar: Array<HotbarSlot | null>;
  /** Monstros especiais que o personagem pode atacar (desligado: nunca causa dano neles). */
  elite: { super: boolean; mega: boolean; ultra: boolean };
  /** Tamanhos de box que a caça automática farma (as outras ela só atravessa pelo corredor). */
  boxSizes: number[];
  /** Condições que o Auto Helper cura sozinho (aba Conditions) */
  cures: Record<string, boolean>;
  /** Movimentação do jogador na caça automática */
  movement: Movement;
}

/**
 * natural: anda até o alvo e ataca (padrão).
 * lure: junta vários monstros atrás de si (a "box") antes de lutar; para de juntar com pouca vida.
 * distance: fica a pelo menos X quadrados do alvo (só para quem ataca de longe).
 */
export interface Movement { mode: 'natural' | 'lure' | 'distance'; distance: number; lureCount: number; lureStopHp: number }
/** espaços da barra de ações: até 2 barras de 12 no PC (até 3 fileiras de 6 no celular) — pedido dos jogadores, 24/10 */
export const HOTBAR_SIZE = 24;

export const DEFAULT_MOVEMENT: Movement = { mode: 'natural', distance: 3, lureCount: 4, lureStopHp: 60 };

export const slot = (use: HotbarSlot['use'], o: Partial<HotbarSlot> = {}): HotbarSlot =>
  ({ use, auto: true, stat: 'hp', below: 50, minMonsters: 1, minMana: 0, ...o });

export function defaultHelper(vocation: VocationId, potionIds: { health: number; mana: number }): HelperSettings {
  const heal = vocation === 'knight' ? 'exura ico' : 'exura';
  const attack = vocation === 'sorcerer' ? 'exori flam' : vocation === 'druid' ? 'exori frigo' : vocation === 'knight' ? 'exori ico' : vocation === 'paladin' ? 'exori con' : null;
  const bar: Array<HotbarSlot | null> = [
    slot({ spell: heal }, { stat: 'hp', below: 70 }),
    slot({ item: potionIds.health }, { stat: 'hp', below: 50 }),
    slot({ item: potionIds.mana }, { stat: 'mana', below: 40 }),
    attack ? slot({ spell: attack }, { minMana: 50 }) : null,
  ];
  while (bar.length < HOTBAR_SIZE) bar.push(null);
  return { stance: 'balanced', autoEat: true, respectful: true, skipLoot: [], keepItems: [], sellWhenFull: true, autoSell: true, hotbar: bar, elite: { super: false, mega: false, ultra: false }, boxSizes: [2, 4, 5, 6, 7, 8],
    cures: Object.fromEntries(CONDITION_CURES.map((c) => [c.id, true])), movement: { ...DEFAULT_MOVEMENT } };
}

const pct = (v: any, d: number) => Math.max(0, Math.min(99, Math.round(Number(v ?? d) || 0)));
function cleanSlot(h: any): HotbarSlot | null {
  if (!h || typeof h !== 'object') return null;
  const src = h.use ?? h;
  const use = typeof src.spell === 'string' ? { spell: String(src.spell).slice(0, 40) } : Number.isInteger(src.item) ? { item: src.item } : null;
  if (!use) return null;
  return {
    use, auto: h.auto !== false && h.enabled !== false, stat: h.stat === 'mana' ? 'mana' : 'hp',
    below: Math.max(1, Math.min(100, Math.round(Number(h.below ?? 50) || 50))),
    minMonsters: Math.max(1, Math.min(10, Math.round(Number(h.minMonsters) || 1))), minMana: pct(h.minMana, 0),
    ...(h.minHarmony !== undefined && h.minHarmony !== null ? { minHarmony: Math.max(0, Math.min(5, Math.round(Number(h.minHarmony) || 0))) } : {}),
    ...(typeof h.who === 'string' && h.who.trim() ? { who: h.who.trim().slice(0, 30) } : {}),
  };
}

/** Limpa o que veio do navegador (nunca confia no formato). Converte o formato antigo (listas de regras). */
export function sanitizeHelper(raw: any, fallback: HelperSettings): HelperSettings {
  if (!raw || typeof raw !== 'object') return fallback;
  const stance: Stance = ['offensive', 'balanced', 'defensive'].includes(raw.stance) ? raw.stance : fallback.stance;
  const skipLoot = Array.isArray(raw.skipLoot) ? raw.skipLoot.filter((x: any) => Number.isInteger(x)).slice(0, 500) : [];
  let hotbar: Array<HotbarSlot | null>;
  const hasNew = Array.isArray(raw.hotbar) && raw.hotbar.some((h: any) => h && (h.use || h.spell || h.item));
  if (hasNew) hotbar = Array.from({ length: HOTBAR_SIZE }, (_, i) => cleanSlot(raw.hotbar[i]));
  else if (Array.isArray(raw.rules) || Array.isArray(raw.attack) || raw.attackSpell) {
    const old = [...(raw.rules ?? []), ...(raw.attack ?? []), ...(raw.attackSpell ? [{ use: { spell: raw.attackSpell }, minMana: raw.attackMinMana }] : [])];
    hotbar = Array.from({ length: HOTBAR_SIZE }, (_, i) => cleanSlot(old[i]));
  } else hotbar = fallback.hotbar;
  while (hotbar.length < HOTBAR_SIZE) hotbar = [...hotbar, null];
  const keepItems = Array.isArray(raw.keepItems) ? raw.keepItems.filter((x: any) => Number.isInteger(x)).slice(0, 500) : [];
  const e = raw.elite && typeof raw.elite === 'object' ? raw.elite : {};
  const elite = { super: e.super === true, mega: e.mega === true, ultra: e.ultra === true };
  const boxSizes = Array.isArray(raw.boxSizes) ? [...new Set(raw.boxSizes.filter((x: any) => [2, 4, 5, 6, 7, 8].includes(x)))] as number[] : [2, 4, 5, 6, 7, 8];
  const rc = raw.cures && typeof raw.cures === 'object' ? raw.cures : {};
  const cures = Object.fromEntries(CONDITION_CURES.map((c) => [c.id, rc[c.id] !== false]));
  const mv = raw.movement && typeof raw.movement === 'object' ? raw.movement : {};
  const clamp = (v: any, lo: number, hi: number, d: number) => Math.max(lo, Math.min(hi, Math.round(Number(v ?? d) || d)));
  const movement: Movement = {
    mode: ['natural', 'lure', 'distance'].includes(mv.mode) ? mv.mode : 'natural',
    distance: clamp(mv.distance, 2, 6, DEFAULT_MOVEMENT.distance), lureCount: clamp(mv.lureCount, 2, 30, DEFAULT_MOVEMENT.lureCount),
    lureStopHp: clamp(mv.lureStopHp, 10, 95, DEFAULT_MOVEMENT.lureStopHp),
  };
  return { stance, autoEat: raw.autoEat !== false, respectful: raw.respectful !== false, skipLoot, keepItems, sellWhenFull: raw.sellWhenFull !== false, autoSell: raw.autoSell === undefined ? fallback.autoSell ?? true : !!raw.autoSell, autoSkillPotion: raw.autoSkillPotion === undefined ? fallback.autoSkillPotion ?? false : !!raw.autoSkillPotion, autoMagicShield: raw.autoMagicShield === undefined ? fallback.autoMagicShield ?? false : !!raw.autoMagicShield, pvpReact: ['fight', 'flee', 'ignore'].includes(raw.pvpReact) ? raw.pvpReact : fallback.pvpReact ?? 'fight', antiRed: raw.antiRed === undefined ? fallback.antiRed ?? true : !!raw.antiRed, hotbar, elite, boxSizes, cures, movement };
}

/** Intervalo de regeneração em segundos (1 ponto por vez), vocation sem promoção, alimentado. */
export const REGEN: Record<VocationId, { hp: number; mana: number }> = {
  knight: { hp: 6, mana: 6 },
  paladin: { hp: 8, mana: 4 },
  sorcerer: { hp: 12, mana: 3 },
  druid: { hp: 12, mana: 3 },
  monk: { hp: 6, mana: 6 },
};
/** Promovidos regeneram mais rápido (vocations.xml oficial: Elite Knight 4s de vida, Master Sorcerer 2s de mana...). */
export const REGEN_PROMOTED: Record<VocationId, { hp: number; mana: number }> = {
  knight: { hp: 4, mana: 6 },
  paladin: { hp: 6, mana: 3 },
  sorcerer: { hp: 12, mana: 2 },
  druid: { hp: 12, mana: 2 },
  monk: { hp: 4, mana: 6 },
};

/** Multiplicadores dos modos de ataque. */
export const STANCE = {
  offensive: { dmg: 1.2, taken: 1.15 },
  balanced: { dmg: 1.0, taken: 1.0 },
  defensive: { dmg: 0.6, taken: 0.75 },
};

/** Venda na hunt: free a cada 20 minutos, VIP a cada 10. Caça offline: free 6 h, VIP 8 h. */
export const HUNT_SELL_COOLDOWN = { free: 20 * 60_000, vip: 10 * 60_000 };
export const OFFLINE_HOURS = { free: 6, vip: 8 };
